import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { X, MessageSquare, Phone, Send, AtSign, Loader2 } from "lucide-react";
import { campaignsRequest } from "../../requests/vendor/CampaignsRequest";
import { toast } from "react-toastify";

const TestSmsModal = ({ open, onClose, testData, onConfirm, setIsTestSmsModalOpen, sender }) => {
  const {
    phoneNumbers,
    senderOptions,
    previewMessage,
    shorten_url,
    campaignSlug,
  } = testData;
  const [selectedSender, setSelectedSender] = React.useState(sender || "");
  const [isLoading, setIsLoading] = React.useState(false);
  
  // Get the sender name for display
  const senderName = React.useMemo(
    () =>
      senderOptions.find((s) => s.id === selectedSender)?.name ||
    "Not selected",
    [selectedSender, senderOptions]
  );
  
  const handleTestSmsConfirm = async () => {
    try {
      const phone_numbers = phoneNumbers.join("," );
      const data = {
        msisdns: phone_numbers,
        sender_id: senderName,
      };
      setIsLoading(true);
      const response = await campaignsRequest.sendTestCampaign(
        campaignSlug,
        data
      );
      if (
        response?.data?.status_code === "000" &&
        response?.data?.in_error === false
      ) {
        setIsLoading(false);
        toast.success("Test SMS sent successfully");
        setIsTestSmsModalOpen(false);
        if (onConfirm) onConfirm();
      } else {
        setIsLoading(false);
        toast.error(response?.data?.reason);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.reason ||
          "An error occurred while sending the test SMS"
      );
    } finally {
      setIsLoading(false);
      setIsTestSmsModalOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: "16px",
        },
      }}
    >
      <DialogTitle className="flex justify-between items-center">
        <div className="flex gap-3 items-center">
          <div className="p-2 bg-green-50 rounded-lg">
            <MessageSquare className="w-5 h-5 text-green-600" />
          </div>
          <span className="text-xl font-semibold">Confirm Test SMS</span>
        </div>
        <IconButton onClick={onClose} size="small">
          <X className="w-5 h-5" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <div className="py-4 space-y-6">
          {/* Sender Selection */}
          <div className="space-y-3">
            <div className="flex gap-2 items-center">
              <AtSign className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Select Sender ID
              </span>
            </div>
            <FormControl fullWidth size="small">
              <Select
                value={selectedSender}
                onChange={(e) => setSelectedSender(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#22C55E",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#22C55E",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <span className="text-gray-400">
                    Choose a sender for your test SMS
                  </span>
                </MenuItem>
                {senderOptions.map((sender) => (
                  <MenuItem
                    key={sender.id}
                    value={sender.id}
                    sx={{
                      borderRadius: "8px",
                      margin: "4px",
                      "&:hover": {
                        backgroundColor: "#F0FDF4",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#F0FDF4",
                        "&:hover": {
                          backgroundColor: "#DCFCE7",
                        },
                      },
                    }}
                  >
                    {sender.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Preview Section */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-700">Message Preview</h3>
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex gap-2 items-center mb-2">
                <Send className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-600">
                  From: {senderName}
                </span>
              </div>
              <p className="text-gray-800">{previewMessage}</p>
              <p className="text-sm italic text-gray-500">{shorten_url}</p>
            </div>
          </div>

          {/* Recipients Section */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-700">Test Recipients</h3>
            <div className="space-y-2">
              {phoneNumbers.map((phone, index) => (
                <div
                  key={index}
                  className="flex gap-3 items-center p-3 bg-green-50 rounded-lg"
                >
                  <Phone className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-700">{phone}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cost Info */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Test SMS Cost</span>
              <span className="font-medium text-gray-900">
                GHC {(phoneNumbers.length * 0.05).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions className="p-6 pt-0">
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#E5E7EB",
            color: "#374151",
            "&:hover": {
              borderColor: "#D1D5DB",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleTestSmsConfirm}
          variant="contained"
          disabled={!selectedSender}
          startIcon={<Send className="w-4 h-4" />}
          sx={{
            backgroundColor: "#22C55E",
            "&:hover": {
              backgroundColor: "#16A34A",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : ""}
          {isLoading ? "Sending..." : "Send Test SMS"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestSmsModal;
